// src/components/organisms/ReportGeneratorDialog.jsx
import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import DateRangeSelector from "../molecules/DateRangeSelector";
import ModeToggle from "../molecules/ModeToggle";
import { generateCoverageReport } from "../../api/adminService";

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 14);
  const fmt = (d) => d.toISOString().split("T")[0];
  return { startDate: fmt(start), endDate: fmt(end) };
}

function validateDates(startDate, endDate) {
  const errors = {};
  if (!startDate) errors.start = "Required";
  if (!endDate) errors.end = "Required";
  if (startDate && endDate) {
    const s = new Date(startDate);
    const e = new Date(endDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (e <= s) errors.end = "End date must be after start date";
    if (e > today) errors.end = "End date cannot be in the future";
    if ((e - s) / (1000 * 60 * 60 * 24) > 365) errors.start = "Range cannot exceed 365 days";
  }
  return errors;
}

/**
 * Dialog for generating a Coverage Report PDF for an organization.
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   organization — { id, name, market_city, market_state }
 */
export default function ReportGeneratorDialog({ open, onClose, organization }) {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [mode, setMode] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateErrors, setDateErrors] = useState({});

  const marketLabel = organization?.market_city
    ? `${organization.market_city}, ${organization.market_state}`
    : "Unassigned Market";

  const handleClose = () => {
    if (loading) return;
    setError("");
    setDateErrors({});
    onClose();
  };

  const handleGenerate = async () => {
    const errors = validateDates(startDate, endDate);
    setDateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError("");

    try {
      const blob = await generateCoverageReport(organization.id, {
        start_date: startDate,
        end_date: endDate,
        mode,
      });

      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeOrgName = organization.name.replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30);
      const today = new Date().toISOString().split("T")[0];
      link.href = url;
      link.download = `Coverage_Report_${safeOrgName}_${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      handleClose();
    } catch (e) {
      setError(e.message || "Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Generate Coverage Report</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Org info */}
          <Stack spacing={1}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                <strong>Organization:</strong> {organization?.name}
              </Typography>
            </Alert>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                <strong>Market:</strong> {marketLabel}
              </Typography>
            </Alert>
          </Stack>

          {/* Date range */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              OBSERVATION WINDOW
            </Typography>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartChange={(e) => { setStartDate(e.target.value); setDateErrors({}); }}
              onEndChange={(e) => { setEndDate(e.target.value); setDateErrors({}); }}
              startError={dateErrors.start}
              endError={dateErrors.end}
              disabled={loading}
            />
          </Box>

          {/* Mode toggle */}
          <ModeToggle mode={mode} onChange={setMode} disabled={loading} />

          {/* Error */}
          {error && <Alert severity="error">{error}</Alert>}

          {/* Loading */}
          {loading && <LinearProgress />}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={loading}
          variant="contained"
          startIcon={<DownloadRoundedIcon />}
        >
          {loading ? "Generating…" : "Generate PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
